import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, ArrowRight } from '@phosphor-icons/react';
import { useFavourites } from '../../context/FavouritesContext';
import { formatCostShort } from '../../lib/format';

const PlanCard = ({ plan, index = 0 }) => {
  const { isFavourite, toggleFavourite } = useFavourites();
  const fav = isFavourite(plan.plan_id);

  return (
    <Link
      to={`/plans/${plan.reference_number || plan.plan_id}`}
      className="block bg-white border border-border hover:-translate-y-1 transition-transform group"
      data-testid={`plan-card-${plan.plan_id}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={plan.image_url}
          alt={plan.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFavourite(plan.plan_id); }}
          className={`absolute top-3 right-3 p-2 rounded-full transition-colors ${fav ? 'bg-red-500 text-white' : 'bg-white/95 text-gray-700 hover:bg-white'}`}
          data-testid={`favourite-card-${plan.plan_id}`}
          aria-label="Toggle favourite"
        >
          <Heart size={18} weight={fav ? 'fill' : 'regular'} />
        </button>
      </div>
      <div className="p-6">
        <div className="flex items-start justify-between mb-2">
          <h3 className="text-lg font-bold text-primary line-clamp-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            {plan.name}
          </h3>
          <span
            className="text-xs font-mono bg-gray-100 px-2 py-1 rounded ml-2 flex-shrink-0"
            style={{ fontFamily: 'JetBrains Mono, monospace' }}
          >
            {plan.plan_type}
          </span>
        </div>
        {plan.reference_number && (
          <div className="text-[10px] font-mono text-text_muted mb-2 uppercase tracking-wider" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            Ref: {plan.reference_number}
          </div>
        )}
        <p className="text-sm text-text_muted mb-4 line-clamp-2">{plan.description}</p>

        <div className="grid grid-cols-3 gap-1.5 mb-3 text-[11px]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
          <div className="border border-border p-2 text-center">
            <div className="text-text_muted text-[9px] uppercase">Stand</div>
            <div className="font-bold text-primary">{plan.min_stand_size_sqm > 0 ? `${plan.min_stand_size_sqm}m²` : '—'}</div>
          </div>
          <div className="border border-border p-2 text-center">
            <div className="text-text_muted text-[9px] uppercase">Beds</div>
            <div className="font-bold text-primary">{plan.bedrooms}</div>
          </div>
          <div className="border border-border p-2 text-center">
            <div className="text-text_muted text-[9px] uppercase">Baths</div>
            <div className="font-bold text-primary">{plan.bathrooms || 1}</div>
          </div>
          <div className="border border-border p-2 text-center">
            <div className="text-text_muted text-[9px] uppercase">Floors</div>
            <div className="font-bold text-primary">{plan.floors}</div>
          </div>
          <div className="border border-border p-2 text-center">
            <div className="text-text_muted text-[9px] uppercase">Est. Cost</div>
            <div className="font-bold text-primary">{formatCostShort(plan.estimated_construction_cost)}</div>
          </div>
          <div className="border border-border p-2 text-center">
            <div className="text-text_muted text-[9px] uppercase">Area</div>
            <div className="font-bold text-primary">{plan.area_sqm}m²</div>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs text-text_muted mb-1">Starting from</div>
            <div className="text-2xl font-bold text-accent" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              R{plan.base_price.toFixed(2)}
            </div>
          </div>
          <div className="flex items-center text-accent text-sm font-medium group-hover:translate-x-2 transition-transform">
            <span>View</span>
            <ArrowRight size={16} weight="bold" className="ml-1" />
          </div>
        </div>
      </div>
    </Link>
  );
};

export default PlanCard;
